import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import posthog from 'posthog-js';
import { RootState } from 'store/rootReducers';

// Ordered most-specific first so prefix matches don't shadow deeper routes.
// Rules: no dynamic ID segments (/issues/:id, /sites/:key etc.) — list/overview pages only.
// Patterns ending with $ are exact to prevent ID sub-paths leaking through.
// Keep in sync with src/routes/admin-routes.jsx and src/routes/LmsRoutes.tsx.
const ROUTE_MAP: Array<{ pattern: RegExp; page: string }> = [
	// Dashboards — operational sub-routes before /operational itself
	{ pattern: /^\/analytics\/operational\/issue-insights$/, page: 'dashboard_issue_insights' },
	{ pattern: /^\/analytics\/operational\/report-insights$/, page: 'dashboard_report_insights' },
	{ pattern: /^\/analytics\/operational$/, page: 'dashboard_operational' },
	{ pattern: /^\/analytics\/executive$/, page: 'dashboard_executive' },
	{ pattern: /^\/analytics\/qc-performance$/, page: 'dashboard_qc' },
	{ pattern: /^\/analytics\/overview$/, page: 'dashboard_analytics_overview' },
	{ pattern: /^\/analytics\/trends$/, page: 'dashboard_trends' },
	{ pattern: /^\/analytics\/sites$/, page: 'dashboard_sites_analytics' },
	{ pattern: /^\/analytics\/issues$/, page: 'dashboard_issues_analytics' },
	{ pattern: /^\/analytics\/auditors$/, page: 'dashboard_auditors_analytics' },
	{ pattern: /^\/analytics\/inventory$/, page: 'dashboard_inventory_analytics' },
	{ pattern: /^\/analytics\/metrics$/, page: 'dashboard_metrics' },
	{ pattern: /^\/analytics\/health$/, page: 'dashboard_health' },
	{ pattern: /^\/analytics\/comparison$/, page: 'dashboard_comparison' },
	{ pattern: /^\/analytics\/customer-feedback$/, page: 'dashboard_customer_feedback' },
	{ pattern: /^\/analytics\/admin-dashboard$/, page: 'dashboard_admin' },
	{ pattern: /^\/analytics\/calculation$/, page: 'dashboard_calculation' },

	// Core entities — list pages only, no detail routes with IDs
	{ pattern: /^\/admin\/sites$/, page: 'sites_list' },
	{ pattern: /^\/admin\/sites\/reports$/, page: 'sites_reports' },
	{ pattern: /^\/admin\/site-groups$/, page: 'site_groups' },
	{ pattern: /^\/admin\/departments$/, page: 'departments_list' },
	{ pattern: /^\/admin\/auditors$/, page: 'users_list' },
	{ pattern: /^\/admin\/auditors\/useroffdays-dashboard$/, page: 'user_offdays_dashboard' },
	{ pattern: /^\/admin\/questionnaire-templates$/, page: 'questionnaire_templates' },
	{ pattern: /^\/admin\/questionnaires$/, page: 'questionnaires_list' },
	{ pattern: /^\/issues$/, page: 'issues_list' },
	{ pattern: /^\/reportsHub$/, page: 'reports_hub' },
	{ pattern: /^\/admin\/bulk-schedule$/, page: 'bulk_schedule' },
	{ pattern: /^\/admin\/bulk-operations$/, page: 'bulk_operations' },

	// Other feature modules — exact or known static sub-paths only
	{ pattern: /^\/gallery$/, page: 'gallery' },
	{ pattern: /^\/repository$/, page: 'repository' },
	{ pattern: /^\/admin\/skuManagement$/, page: 'sku_management' },
	{ pattern: /^\/admin\/stockAuditScheduler$/, page: 'stock_audit_scheduler' },
	{ pattern: /^\/admin\/customerFeedback\/dashboard$/, page: 'customer_feedback_dashboard' },
	{ pattern: /^\/admin\/customerFeedback\/theme-settings$/, page: 'customer_feedback_theme' },
	{ pattern: /^\/admin\/customerFeedback\/qr-editor$/, page: 'customer_feedback_qr_editor' },
	{ pattern: /^\/admin\/customerFeedback$/, page: 'customer_feedback' },
	{ pattern: /^\/admin\/asset-tracker$/, page: 'asset_tracker' },
	{ pattern: /^\/admin\/promotions$/, page: 'promotions' },
	{ pattern: /^\/admin\/salestarget$/, page: 'sales_target' },
	{ pattern: /^\/admin\/formula-builder$/, page: 'formula_builder' },
	{ pattern: /^\/admin\/data-explorer$/, page: 'data_explorer' },
	{ pattern: /^\/admin\/competitorAnalysis$/, page: 'competitor_analysis' },
	{ pattern: /^\/settings\/role-manager$/, page: 'settings_role_manager' },
	{ pattern: /^\/settings\/feature-access(-v2)?$/, page: 'settings_feature_access' },
	{ pattern: /^\/settings\/permissions$/, page: 'settings_permissions' },
	{ pattern: /^\/settings\/integration$/, page: 'settings_integration' },
	{ pattern: /^\/admin\/offdays$/, page: 'off_days' },
	{ pattern: /^\/help$/, page: 'help' },
	{ pattern: /^\/home$/, page: 'home' },

	// LMS — list and manage pages only, no course/syllabus detail IDs
	{ pattern: /^\/nimblylearn\/dashboard$/, page: 'lms_dashboard' },
	{ pattern: /^\/nimblylearn\/users$/, page: 'lms_users' },
	{ pattern: /^\/nimblylearn\/courses-and-syllabus$/, page: 'lms_courses_syllabus' },
	{ pattern: /^\/nimblylearn\/courses-and-syllabus\/manage(\/[^/]+)?$/, page: 'lms_cs_manage' },
	{ pattern: /^\/nimblylearn\/quiz\/manage$/, page: 'lms_quiz_manage' },
	{ pattern: /^\/nimblylearn\/user-groups$/, page: 'lms_user_groups' },
];

function resolvePageName(pathname: string): string | null {
	for (const { pattern, page } of ROUTE_MAP) {
		if (pattern.test(pathname)) return page;
	}
	return null;
}

export function usePageTracking(isIdentified: boolean) {
	const pathname = useSelector((state: RootState) => state.router.location.pathname);

	useEffect(() => {
		if (!isIdentified) return;

		const page = resolvePageName(pathname);
		if (!page) return;

		posthog.capture('page_viewed', { page, path: pathname });
	}, [pathname, isIdentified]);
}
